import { queryKeys } from "@/client";
import { useAuth } from "@/contexts/AuthHook";
import { type User } from "@precedent/db-types/src/schema";
import { type SupabaseClient } from "@supabase/supabase-js";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

export function usePageTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

// Define a type for the user profile data we need
export type UserProfile = Pick<
  User,
  "id" | "first_name" | "last_name" | "email" | "avatar_url"
>;

/**
 * A hook to fetch profile data for an array of user IDs.
 * It efficiently batches the request and returns a map for easy lookups.
 * @param userIds - An array of user UUIDs to fetch profiles for.
 */
export const useUserProfiles = (userIds: string[]) => {
  const { supabase } = useAuth();

  return useQuery({
    queryKey: queryKeys.userProfiles(userIds),

    queryFn: async (): Promise<Record<string, UserProfile>> => {
      if (!userIds || userIds.length === 0) {
        return {};
      }

      const { data, error } = await (supabase as SupabaseClient)
        .from("user") // Your profiles table name
        .select("id, first_name, last_name, email, avatar_url")
        .in("id", userIds);

      if (error) {
        throw new Error("Failed to fetch user profiles: " + error.message);
      }

      // Convert the array of profiles into a map (object) for fast lookups,
      // e.g., { 'user-uuid-123': { user_id: '...', first_name: '...' } }
      const profilesMap = data.reduce<Record<string, UserProfile>>(
        (acc, profile) => {
          acc[profile.id] = profile;
          return acc;
        },
        {},
      );

      return profilesMap;
    },
    // Keep the data fresh but don't refetch aggressively.
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Keep data around so we don't have to re-fetch constantly
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: userIds.length > 0,
  });
};
